/**
 * Reusable FileUpload component.
 *
 * Flow:
 * 1. User picks file
 * 2. Frontend asks backend: POST /api/storage/signed-upload-url { bucket, path }
 * 3. Backend returns signed_url + token
 * 4. Frontend PUTs file directly to Supabase via signed_url (bypasses FastAPI)
 * 5. Frontend tells backend: POST /api/storage/metadata { bucket, path, attached_to, attached_id, ... }
 * 6. Backend records the upload in Mongo (file_uploads collection)
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, X, FileText, Image as ImageIcon, Film, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_FOR = (type) => {
  if (!type) return FileText;
  if (type.startsWith('image/')) return ImageIcon;
  if (type.startsWith('video/')) return Film;
  return FileText;
};

const formatBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export const FileUpload = ({
  bucket,
  attachedTo,        // 'creator' | 'campaign' | 'deliverable' | 'brand_brief' | 'invoice'
  attachedId,
  accept = '',       // e.g. 'application/pdf,image/*'
  maxBytes,          // e.g. 10 * 1024 * 1024
  label = 'Upload file',
  onUploaded,        // (fileMetadata) => void
  compact = false,
}) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);

  const pickFile = (f) => {
    if (!f) return;
    if (maxBytes && f.size > maxBytes) {
      toast.error(`File too large. Max ${formatBytes(maxBytes)}`);
      return;
    }
    setFile(f);
    setDone(false);
    setProgress(0);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(0);

    try {
      // Step 1: ask backend for a signed upload URL
      const { data: signed } = await axios.post(
        `${API_URL}/api/storage/signed-upload-url`,
        { bucket, path: file.name, content_type: file.type },
        { withCredentials: true }
      );

      if (!signed.signed_url) throw new Error('No signed URL returned');

      // Step 2: upload directly to Supabase (PUT)
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', signed.signed_url);
        if (file.type) xhr.setRequestHeader('Content-Type', file.type);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload ${xhr.status}`)));
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(file);
      });

      // Step 3: record metadata in Mongo
      const { data: meta } = await axios.post(
        `${API_URL}/api/storage/metadata`,
        {
          bucket,
          path: signed.path,
          filename: file.name,
          size: file.size,
          content_type: file.type,
          attached_to: attachedTo,
          attached_id: attachedId,
        },
        { withCredentials: true }
      );

      setDone(true);
      toast.success(`Uploaded: ${file.name}`);
      onUploaded?.(meta);
      setTimeout(() => { setFile(null); setProgress(0); setDone(false); }, 1500);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const Icon = file ? ICON_FOR(file.type) : Upload;

  if (compact) {
    return (
      <div className="inline-flex items-center gap-2" data-testid="file-upload-compact">
        <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} data-testid="file-upload-input" />
        {!file ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs text-zinc-300 transition-colors"
            data-testid="file-upload-pick-button"
          >
            <Upload size={12} /> {label}
          </button>
        ) : (
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs">
            <Icon size={12} className="text-[#FF451A]" />
            <span className="text-white truncate max-w-[120px]">{file.name}</span>
            {!uploading && !done && (
              <>
                <button onClick={upload} className="text-[#FF451A] hover:text-white font-medium" data-testid="file-upload-start-button">Upload</button>
                <button onClick={() => setFile(null)} className="text-zinc-500 hover:text-white"><X size={12} /></button>
              </>
            )}
            {uploading && <span className="text-zinc-400 flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> {progress}%</span>}
            {done && <Check size={12} className="text-[#00C972]" />}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border-2 border-dashed border-white/10 rounded-xl p-4 bg-white/[0.02] hover:bg-white/[0.04] transition-colors" data-testid="file-upload">
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} data-testid="file-upload-input" />

      {!file ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full flex flex-col items-center gap-2 py-4 text-zinc-400 hover:text-white transition-colors"
          data-testid="file-upload-pick-button"
        >
          <Upload size={24} />
          <span className="text-sm font-medium">{label}</span>
          {maxBytes && <span className="text-xs text-zinc-600 font-mono">Max {formatBytes(maxBytes)}</span>}
        </button>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Icon size={20} className="text-[#FF451A] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{file.name}</p>
              <p className="text-xs text-zinc-500 font-mono">{formatBytes(file.size)}</p>
            </div>
            {!uploading && !done && (
              <button onClick={() => setFile(null)} className="text-zinc-500 hover:text-white" data-testid="file-upload-clear-button">
                <X size={16} />
              </button>
            )}
            {done && <Check size={18} className="text-[#00C972]" />}
          </div>

          {uploading && (
            <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-[#FF451A] transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}

          {!uploading && !done && (
            <button
              onClick={upload}
              className="w-full bg-[#FF451A] hover:bg-[#E63E17] text-white text-sm font-medium rounded-lg py-2 transition-colors"
              data-testid="file-upload-start-button"
            >
              Upload to Supabase
            </button>
          )}
        </div>
      )}
    </div>
  );
};
